import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from '@/services/adminService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Eye,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const TX_TYPES = {
  deposit: 'Depozito',
  installment: 'Taksit',
  final_payment: 'Son Ödeme',
  refund: 'İade',
  commission: 'Komisyon',
  tax: 'Vergi',
  other: 'Diğer',
};

const TX_STATUS = {
  pending: { label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-800' },
  completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-800' },
  failed: { label: 'Başarısız', color: 'bg-red-100 text-red-800' },
  cancelled: { label: 'İptal', color: 'bg-gray-100 text-gray-800' },
};

const PAGE_SIZE = 20;

export default function AdminTransactionsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    const { data, count, error } = await adminService.getTransactions({
      status: statusFilter === 'all' ? null : statusFilter,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    });
    if (error) {
      toast({ title: 'Hata', description: 'İşlemler yüklenemedi', variant: 'destructive' });
    } else {
      setTransactions(data || []);
      setTotalCount(count || 0);
    }
    setLoading(false);
  }, [statusFilter, page, toast]);

  useEffect(() => { loadData(); }, [loadData]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
  const formatCurrency = (a, cur) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: cur || 'GBP', maximumFractionDigits: 0 }).format(a || 0);

  // Toplam tamamlanan tutar
  const totalCompleted = transactions
    .filter((tx) => tx.status === 'completed')
    .reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="flex gap-3">
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Durum" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Durumlar</SelectItem>
                  {Object.entries(TX_STATUS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={loadData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            {totalCompleted > 0 && (
              <div className="text-sm">
                Sayfa Toplamı (tamamlanan): <span className="font-bold text-green-700">{formatCurrency(totalCompleted)}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Finansal İşlemler ({totalCount})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>İşlem bulunamadı</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kullanıcı</TableHead>
                      <TableHead>Tür</TableHead>
                      <TableHead>Tutar</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Ödeme Yöntemi</TableHead>
                      <TableHead>Referans</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead className="text-right">Detay</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((tx) => {
                      const st = TX_STATUS[tx.status] || TX_STATUS.pending;
                      return (
                        <TableRow key={tx.id}>
                          <TableCell>
                            <button
                              onClick={() => navigate(`/admin/users/${tx.user_id}`)}
                              className="text-blue-600 hover:underline font-medium"
                            >
                              {tx.user_profile?.full_name || tx.user_id?.slice(0, 8)}
                            </button>
                          </TableCell>
                          <TableCell>{TX_TYPES[tx.transaction_type] || tx.transaction_type}</TableCell>
                          <TableCell className="font-medium">{formatCurrency(tx.amount, tx.currency)}</TableCell>
                          <TableCell>
                            <Badge className={`${st.color} text-xs`}>{st.label}</Badge>
                          </TableCell>
                          <TableCell>{tx.payment_method || '-'}</TableCell>
                          <TableCell className="text-xs max-w-[120px] truncate">{tx.reference_number || '-'}</TableCell>
                          <TableCell className="text-xs">{formatDate(tx.transaction_date)}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/users/${tx.user_id}`)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">{page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, totalCount)} / {totalCount}</p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                    <span className="text-sm">{page + 1} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <Toaster />
    </div>
  );
}
